<template>
  <div class="erp-approved-consums-grid">
    <div class="approved-view-switcher" role="tablist">
      <button
        type="button"
        role="tab"
        class="btn btn-sm"
        :class="activeView === 'batches' ? 'btn-primary' : 'btn-outline-primary'"
        :aria-selected="activeView === 'batches' ? 'true' : 'false'"
        @click="switchView('batches')"
      >
        {{ $t("tools.erp_integration.consum_batch_view") }}
      </button>
      <button
        type="button"
        role="tab"
        class="btn btn-sm"
        :class="activeView === 'items' ? 'btn-primary' : 'btn-outline-primary'"
        :aria-selected="activeView === 'items' ? 'true' : 'false'"
        @click="switchView('items')"
      >
        {{ $t("tools.erp_integration.consum_items_view") }}
      </button>
    </div>
    <ApprovedConsumBatches
      v-if="activeView === 'batches'"
      ref="batches"
      @open-audit="$emit('open-audit', $event)"
    />
    <label v-if="activeView === 'items'" class="mb-3">
      <input v-model="unsentOnly" type="checkbox" @change="onAvailabilityChanged" />
      {{ $t("tools.erp_integration.consum_available_only") }}
    </label>
    <DxDataGrid
      v-if="activeView === 'items'"
      ref="grid"
      class="smagria-grid erp-consum-grid"
      :data-source="dataSource"
      key-expr="uuid"
      :remote-operations="gridRemoteOperations"
      :column-min-width="110"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="false"
      :row-alternation-enabled="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :no-data-text="$t('common.no_data_text')"
      style="min-height: 320px"
      @selection-changed="onSelectionChanged"
      @editor-preparing="onEditorPreparing"
    >
      <DxPaging :page-size="pageSizes[0]" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxSelection mode="multiple" show-check-boxes-mode="always" select-all-mode="page" />
      <DxFilterRow :visible="true" :show-operation-chooser="true" apply-filter="auto" />
      <DxHeaderFilter :visible="true" />
      <DxScrolling mode="standard" column-rendering-mode="standard" show-scrollbar="always" use-native="true" />
      <DxGroupPanel :visible="false" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxExport :enabled="false" :formats="['xlsx']" :allow-export-selected-data="false" file-name="ApprovedConsums" />
      <DxToolbar>
        <DxToolbarItem location="after" template="reloadTemplate" />
        <DxToolbarItem location="after" template="sendToErpTemplate" />
      </DxToolbar>
      <DxColumn
        data-field="erp_status"
        :caption="$t('tools.erp_integration.consum_erp_status')"
        cell-template="erpStatusTemplate"
        width="135"
        :allow-filtering="true"
        :allow-sorting="false"
      />
      <DxColumn data-field="consum_date" :caption="$t('tools.erp_integration.consum_date')" data-type="date" format="yyyy-MM-dd" />
      <DxColumn data-field="product_name" :caption="$t('common.product')" :allow-sorting="false" />
      <DxColumn data-field="quantity" :caption="$t('tools.erp_integration.consum_quantity')" data-type="number" format="#,##0.####" />
      <DxColumn data-field="measure_unit_name" :caption="$t('common.measure_unit')" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="unit_price" :caption="$t('tools.erp_integration.consum_unit_price')" data-type="number" format="#,##0.####" />
      <DxColumn data-field="warehouse_name" :caption="$t('common.warehouse')" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="work_sheet_public_id" :caption="$t('tools.erp_integration.work_sheet')" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="work_date" :caption="$t('works.work_approval.work_date')" data-type="date" format="yyyy-MM-dd" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="work_name" :caption="$t('works.work_approval.work_name')" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="parcel_name" :caption="$t('works.work_approval.parcel_name')" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="department_name" :caption="$t('works.work_approval.unit_name')" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="culture_name" :caption="$t('works.work_approval.culture_name')" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="season_name" :caption="$t('works.work_approval.season_name')" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="legal_entity_name" :caption="$t('works.work_approval.legal_entity_name')" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="consum_approved_at" :caption="$t('tools.erp_integration.consum_approved_at')" data-type="datetime" format="yyyy-MM-dd HH:mm" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="work_sheet_approved_at" :caption="$t('tools.erp_integration.work_sheet_approved_at')" data-type="datetime" format="yyyy-MM-dd HH:mm" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="erp_sent_at" :caption="$t('tools.erp_integration.consum_erp_sent_at')" data-type="datetime" format="yyyy-MM-dd HH:mm" :allow-filtering="false" :allow-sorting="false" />
      <DxColumn data-field="erp_batch_uuid" :caption="$t('tools.erp_integration.consum_erp_batch')" cell-template="erpBatchTemplate" :allow-filtering="false" :allow-sorting="false" />

      <template #erpBatchTemplate="{ data }">
        <span v-if="data.data.erp_batch_uuid" :title="data.data.erp_batch_uuid">
          {{ data.data.erp_batch_uuid.slice(0, 8) }}
          ({{ data.data.erp_batch_count }})
        </span>
        <span v-else>-</span>
      </template>

      <template #sendToErpTemplate>
        <button
          type="button"
          class="erp-send-button erp-send-button--sm"
          :disabled="!selectedRowKeys.length"
          @click="openSendToErpDialog"
        >
          {{ $t("tools.erp_integration.consum_send_to_erp") }}
        </button>
      </template>


      <template #reloadTemplate>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          :disabled="reloading"
          @click="reload"
        >
          <i class="fa fa-sync-alt me-1" :class="{ 'fa-spin': reloading }"></i>
          {{ $t("common.refresh") }}
        </button>
      </template>

      <template #erpStatusTemplate="{ data }">
        <div class="erp-status-cell">
          <span
            v-if="data.data.has_consum === false"
            class="erp-badge erp-badge--neutral"
          >
            <span class="erp-badge__label">{{ $t("tools.erp_integration.consum_without_consum") }}</span>
          </span>
          <span
            v-if="data.data.erp_status === 'SUCCESS'"
            class="erp-badge erp-badge--success"
            :title="getSuccessTooltip(data.data)"
          >
            <i class="fa fa-check-circle me-1"></i>
            <span class="erp-badge__label">{{ $t("tools.erp_integration.consum_status_success") }}</span>
          </span>
          <span
            v-else-if="data.data.erp_status === 'PARTIAL_SUCCESS'"
            class="erp-badge erp-badge--partial"
            :title="$t('tools.erp_integration.consum_feedback_partial_heading')"
          >
            <i class="fa fa-exclamation-triangle me-1"></i>
            <span class="erp-badge__label">{{ $t("tools.erp_integration.consum_status_partial_success") }}</span>
          </span>
          <span
            v-else-if="data.data.erp_status === 'FAILED'"
            class="erp-badge erp-badge--danger"
            :title="data.data.erp_last_error || $t('tools.erp_integration.consum_status_failed')"
          >
            <i class="fa fa-exclamation-circle me-1"></i>
            <span class="erp-badge__label">{{ $t("tools.erp_integration.consum_status_failed") }}</span>
          </span>
          <span
            v-else-if="data.data.erp_status === 'PENDING'"
            class="erp-badge erp-badge--neutral"
          >
            <span class="erp-badge__label">{{ $t("tools.erp_integration.consum_work_invoice_pending") }}</span>
          </span>
          <span
            v-else
            class="erp-badge erp-badge--neutral"
          >
            <span class="erp-badge__label">{{ $t("tools.erp_integration.consum_status_not_sent") }}</span>
          </span>
        </div>
      </template>
    </DxDataGrid>

    <ConsumSummaryExportDialog
      :visible.sync="summaryPopupVisible"
      :selected-rows="selectedRows.filter((row) => row.erp_exportable === true)"
      @exported="onConsumsExported"
    />
  </div>
</template>

<script>
import DxDataGrid, {
  DxColumn,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging,
  DxSelection,
  DxScrolling,
  DxGroupPanel,
  DxColumnChooser,
  DxExport,
  DxToolbar,
  DxItem as DxToolbarItem,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import { mapActions } from "vuex";
import { normalizeDateFilter } from "@/helpers/dateFilterHelper";
import ConsumSummaryExportDialog from "./ConsumSummaryExportDialog.vue";
import ApprovedConsumBatches from "./ApprovedConsumBatches.vue";
import notify from "devextreme/ui/notify";

export default {
  name: "ErpApprovedConsumsGrid",
  components: {
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    DxSelection,
    DxScrolling,
    DxGroupPanel,
    DxColumnChooser,
    DxExport,
    DxToolbar,
    DxToolbarItem,
    ConsumSummaryExportDialog,
    ApprovedConsumBatches,
  },
  data() {
    return {
      pageSizes: [15, 50, 100],
      activeView: "batches",
      unsentOnly: false,
      gridRemoteOperations: { paging: true, filtering: true, sorting: true },
      dataSource: new CustomStore({
        key: "uuid",
        load: (loadOptions) => this.loadApprovedConsums(loadOptions),
      }),
      selectedRowKeys: [],
      selectedRows: [],
      summaryPopupVisible: false,
      reloading: false,
    };
  },
  methods: {
    ...mapActions({
      fetchApprovedConsums: "erpIntegration/fetchApprovedConsums",
    }),
    async loadApprovedConsums(loadOptions) {
      const limit = loadOptions.take || 25;
      const skip = loadOptions.skip || 0;
      const response = await this.fetchApprovedConsums({
        page: Math.floor(skip / limit) + 1,
        limit,
        filter: normalizeDateFilter(loadOptions.filter, "consum_date"),
        sort: loadOptions.sort,
        unsent_only: this.unsentOnly ? 1 : 0,
      });
      const rows = (response?.data || []).map((row) => ({
        ...row,
        erp_sent: !!row.erp_sent,
      }));

      return {
        data: rows,
        totalCount: response?.pagination?.total || rows.length,
      };
    },
    async reload() {
      if (this.activeView === "batches") {
        return this.$refs.batches?.reload();
      }
      const dataSource = this.$refs.grid?.instance?.getDataSource();
      if (!dataSource || this.reloading) {
        return;
      }

      this.reloading = true;
      this.clearSelection();
      try {
        await dataSource.reload();
      } finally {
        this.reloading = false;
      }
    },
    onSelectionChanged(event) {
      const eligibleRows = event.selectedRowsData.filter((row) => row.erp_exportable === true);
      this.selectedRows = eligibleRows;
      this.selectedRowKeys = eligibleRows.map((row) => row.uuid);
      const blockedKeys = event.selectedRowsData
        .filter((row) => row.erp_exportable !== true)
        .map((row) => row.uuid);
      if (blockedKeys.length) {
        event.component.deselectRows(blockedKeys);
        notify(this.$t("tools.erp_integration.consum_batch_selection_notice"), "warning", 4000);
      }
    },
    onEditorPreparing(event) {
      if (event.parentType === "dataRow" && event.command === "select" &&
          event.row?.data?.erp_exportable !== true) {
        event.editorOptions.disabled = true;
      }
    },
    clearSelection() {
      this.$refs.grid?.instance?.clearSelection();
      this.selectedRowKeys = [];
      this.selectedRows = [];
    },
    switchView(view) {
      if (view === this.activeView) return;
      this.clearSelection();
      this.summaryPopupVisible = false;
      this.activeView = view;
    },
    async onAvailabilityChanged() {
      this.clearSelection();
      const grid = this.$refs.grid?.instance;
      if (grid?.pageIndex() !== 0) {
        await grid.pageIndex(0);
      } else {
        await this.reload();
      }
    },
    openSendToErpDialog() {
      if (!this.selectedRowKeys.length) {
        return;
      }
      if (this.selectedRows.some((row) => row.erp_exportable !== true)) {
        notify(this.$t("tools.erp_integration.consum_batch_selection_notice"), "warning", 4000);
        return;
      }
      this.summaryPopupVisible = true;
    },
    getSuccessTooltip(row) {
      const parts = [];
      if (row.erp_document_number) parts.push(`Nr: ${row.erp_document_number}`);
      if (row.erp_document_series) parts.push(`Serie: ${row.erp_document_series}`);
      if (row.erp_cod_ies) parts.push(`Cod: ${row.erp_cod_ies}`);
      return parts.length ? parts.join(" | ") : this.$t("tools.erp_integration.consum_status_success");
    },
    onConsumsExported({ clearSelection } = {}) {
      this.reload();
      if (clearSelection) {
        this.$refs.grid?.instance?.clearSelection();
        this.selectedRowKeys = [];
        this.selectedRows = [];
      }
    },
  },
};
</script>

<style scoped>
.approved-view-switcher {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.erp-approved-consums-grid ::v-deep .erp-consum-grid {
  height: auto;
}

::v-deep .dx-datagrid-rowsview .dx-scrollable-content {
  padding-bottom: 16px;
}

.erp-send-button {
  background: #ffffff;
  border: 1px solid #1f7a3f;
  border-radius: 4px;
  color: #1f7a3f;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  padding: 8px 16px;
  transition: all 0.2s ease;
}

.erp-send-button--sm {
  font-size: 13px;
  padding: 6px 12px;
}

.erp-send-button:hover:not(:disabled) {
  background: #f2f9f4;
  border-color: #1f7a3f;
  color: #1f7a3f;
}

.erp-send-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.erp-status-cell {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  white-space: normal;
}

.erp-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
}

.erp-badge i {
  flex-shrink: 0;
}

.erp-badge__label {
  min-width: 0;
  overflow-wrap: anywhere;
}

.erp-badge--success {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.erp-badge--danger {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  cursor: help;
}

.erp-badge--partial {
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fde68a;
  cursor: help;
}

.erp-badge--neutral {
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #e5e7eb;
}
</style>